import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Copy, Clock, MessageSquare, Code, AlertCircle, CheckCircle, Workflow } from "lucide-react";
import { toast } from "sonner";

interface PromptTrace {
  step: string;
  criterion_name: string;
  evaluation_name?: string;
  template_used: string;
  output_template_used?: string;
  prompt_sent: string;
  hardness?: string;
  prompt_policy_version?: string;
  output_schema_version?: string;
  prompt_hash?: string;
  context_hash?: string;
  input_text_hash?: string;
  variables_used: Record<string, any>;
  timestamp?: string;
  tokens_in?: number;
  tokens_out?: number;
  response_received: boolean;
  error?: string;
}

interface PromptTraceViewerProps {
  promptsTrace: unknown;
}

export const PromptTraceViewer = ({ promptsTrace }: PromptTraceViewerProps) => {
  const normalizePromptsTrace = (value: unknown): PromptTrace[] => {
    if (Array.isArray(value)) {
      return value
        .filter((item): item is Record<string, any> => !!item && typeof item === "object")
        .map((item, index) => ({
          step: String(item.step || `step_${index + 1}`),
          criterion_name: String(item.criterion_name || "Analyse globale"),
          evaluation_name: item.evaluation_name ? String(item.evaluation_name) : undefined,
          template_used: String(item.template_used || "analysis_prompt"),
          output_template_used: item.output_template_used
            ? String(item.output_template_used)
            : undefined,
          prompt_sent: String(item.prompt_sent || ""),
          hardness: item.hardness ? String(item.hardness) : undefined,
          prompt_policy_version: item.prompt_policy_version
            ? String(item.prompt_policy_version)
            : undefined,
          output_schema_version: item.output_schema_version
            ? String(item.output_schema_version)
            : undefined,
          prompt_hash: item.prompt_hash ? String(item.prompt_hash) : undefined,
          context_hash: item.context_hash ? String(item.context_hash) : undefined,
          input_text_hash: item.input_text_hash ? String(item.input_text_hash) : undefined,
          variables_used:
            item.variables_used && typeof item.variables_used === "object"
              ? item.variables_used
              : {},
          timestamp: item.timestamp ? String(item.timestamp) : undefined,
          tokens_in: Number(item.tokens_in || 0),
          tokens_out: Number(item.tokens_out || 0),
          response_received: item.response_received !== false,
          error: item.error ? String(item.error) : undefined,
        }));
    }

    if (value && typeof value === "object") {
      return Object.entries(value as Record<string, any>).map(([key, raw]) => {
        const item = raw && typeof raw === "object" ? raw : {};
        const promptFromLegacy =
          typeof item.prompt_sent === "string"
            ? item.prompt_sent
            : typeof item.analysis_user_prompt === "string"
            ? item.analysis_user_prompt
            : "";
        return {
          step: key,
          criterion_name: String(item.criterion_name || "Analyse globale"),
          evaluation_name: item.evaluation_name ? String(item.evaluation_name) : undefined,
          template_used: String(item.template_used || "legacy_prompt_trace"),
          output_template_used: item.output_template_used
            ? String(item.output_template_used)
            : undefined,
          prompt_sent: promptFromLegacy,
          hardness: item.hardness ? String(item.hardness) : undefined,
          prompt_policy_version: item.prompt_policy_version
            ? String(item.prompt_policy_version)
            : undefined,
          output_schema_version: item.output_schema_version
            ? String(item.output_schema_version)
            : undefined,
          prompt_hash: item.prompt_hash ? String(item.prompt_hash) : undefined,
          context_hash: item.context_hash ? String(item.context_hash) : undefined,
          input_text_hash: item.input_text_hash ? String(item.input_text_hash) : undefined,
          variables_used: item,
          timestamp: item.timestamp ? String(item.timestamp) : undefined,
          tokens_in: Number(item.tokens_in || 0),
          tokens_out: Number(item.tokens_out || 0),
          response_received: item.response_received !== false,
          error: item.error ? String(item.error) : undefined,
        };
      });
    }

    return [];
  };

  const normalizedTrace = normalizePromptsTrace(promptsTrace);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié dans le presse-papiers`);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStepIcon = (step: string, responseReceived: boolean, error?: string) => {
    if (error) return <AlertCircle className="h-4 w-4 text-destructive" />;
    if (responseReceived) return <CheckCircle className="h-4 w-4 text-success" />;
    return <MessageSquare className="h-4 w-4 text-muted-foreground" />;
  };

  const totals = normalizedTrace.reduce(
    (acc, trace) => {
      acc.tokensIn += Number(trace.tokens_in || 0);
      acc.tokensOut += Number(trace.tokens_out || 0);
      return acc;
    },
    { tokensIn: 0, tokensOut: 0 }
  );

  const totalTokens = totals.tokensIn + totals.tokensOut;
  const lastUpdate = normalizedTrace[normalizedTrace.length - 1]?.timestamp;

  if (normalizedTrace.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Traçabilité des Prompts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Aucune trace de prompt disponible pour cette analyse.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30">
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Code className="h-5 w-5" />
            Traçabilité des Prompts
          </CardTitle>
          <Badge variant="outline" className="gap-1">
            <Workflow className="h-3.5 w-3.5" />
            {normalizedTrace.length} étape{normalizedTrace.length > 1 ? 's' : ''}
          </Badge>
        </div>
        <CardDescription className="flex items-center gap-2 text-xs sm:text-sm">
          <Badge variant="secondary" className="text-xs">{totalTokens.toLocaleString()} tokens cumulés</Badge>
          <span className="text-muted-foreground">
            Explorez chaque étape d'analyse, les variables substituées et le prompt envoyé.
          </span>
        </CardDescription>
      </CardHeader>
      <TooltipProvider delayDuration={0}>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-md border bg-muted/40 p-3 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tokens d'entrée</p>
              <p className="text-lg font-semibold">{totals.tokensIn.toLocaleString()}</p>
            </div>
            <div className="rounded-md border bg-muted/40 p-3 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Tokens de sortie</p>
              <p className="text-lg font-semibold">{totals.tokensOut.toLocaleString()}</p>
            </div>
            <div className="rounded-md border bg-muted/40 p-3 text-center">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Dernière mise à jour</p>
              <p className="text-sm font-semibold">
                {lastUpdate ? formatDate(lastUpdate) : '—'}
              </p>
            </div>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-3">
            {normalizedTrace.map((trace, index) => (
            <AccordionItem key={index} value={`step-${index}`} className="overflow-hidden rounded-lg border bg-background">
              <AccordionTrigger className="flex flex-col items-start gap-3 px-4 py-3 text-left hover:no-underline sm:flex-row sm:items-center sm:gap-4">
                <div className="flex w-full flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex items-center gap-2">
                    {getStepIcon(trace.step, trace.response_received, trace.error)}
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                      {trace.step}
                    </Badge>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium leading-tight">{trace.criterion_name}</div>
                    {trace.evaluation_name && (
                      <div className="text-xs text-muted-foreground">
                        Évaluation : {trace.evaluation_name}
                      </div>
                    )}
                    <div className="text-xs font-medium text-muted-foreground">
                      {trace.template_used} • {trace.tokens_in + trace.tokens_out} tokens
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{formatDate(trace.timestamp)}</span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="bg-muted/30 px-4 py-5">
                <div className="space-y-6">
                  <div className="grid gap-4 text-sm sm:grid-cols-2">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Template d'analyse</p>
                      <p className="font-medium break-words">{trace.template_used}</p>
                    </div>
                    {trace.hardness && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Profil de dureté</p>
                        <p className="font-medium break-words">{trace.hardness}</p>
                      </div>
                    )}
                    {trace.prompt_policy_version && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Version policy prompt</p>
                        <p className="font-medium break-words">{trace.prompt_policy_version}</p>
                      </div>
                    )}
                    {trace.output_schema_version && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Version schéma sortie</p>
                        <p className="font-medium break-words">{trace.output_schema_version}</p>
                      </div>
                    )}
                    {trace.output_template_used && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Template de sortie</p>
                        <p className="font-medium break-words">{trace.output_template_used}</p>
                      </div>
                    )}
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Statut</p>
                      <Badge variant={trace.error ? "destructive" : trace.response_received ? "secondary" : "outline"}>
                        {trace.error ? "Erreur" : trace.response_received ? "Succès" : "En cours"}
                      </Badge>
                    </div>
                    {trace.evaluation_name && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Évaluation</p>
                        <p className="font-medium break-words">{trace.evaluation_name}</p>
                      </div>
                    )}
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Tokens d'entrée</p>
                      <p className="font-semibold">{trace.tokens_in.toLocaleString()}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Tokens de sortie</p>
                      <p className="font-semibold">{trace.tokens_out.toLocaleString()}</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Variables substituées</h4>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(JSON.stringify(trace.variables_used, null, 2), 'Variables')}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Copier les variables</TooltipContent>
                      </Tooltip>
                    </div>
                    <ScrollArea className="max-h-48 rounded-md border bg-background">
                      <pre className="whitespace-pre-wrap break-words p-4 text-xs font-mono leading-relaxed">
                        {JSON.stringify(trace.variables_used, null, 2)}
                      </pre>
                    </ScrollArea>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    {(trace.prompt_hash || trace.context_hash || trace.input_text_hash) && (
                      <div className="grid gap-3 text-xs sm:grid-cols-3">
                        {trace.prompt_hash && (
                          <div>
                            <p className="text-muted-foreground">Prompt hash</p>
                            <p className="font-mono break-all">{trace.prompt_hash}</p>
                          </div>
                        )}
                        {trace.context_hash && (
                          <div>
                            <p className="text-muted-foreground">Context hash</p>
                            <p className="font-mono break-all">{trace.context_hash}</p>
                          </div>
                        )}
                        {trace.input_text_hash && (
                          <div>
                            <p className="text-muted-foreground">Input hash</p>
                            <p className="font-mono break-all">{trace.input_text_hash}</p>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Prompt envoyé au modèle</h4>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(trace.prompt_sent, 'Prompt complet')}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Copier le prompt</TooltipContent>
                      </Tooltip>
                    </div>
                    <ScrollArea className="max-h-[60vh] rounded-md border bg-background">
                      <pre className="whitespace-pre-wrap break-words p-4 text-sm font-mono leading-relaxed">
                        {trace.prompt_sent || "Prompt non stocké pour ce run (legacy ou traçabilité réduite)."}
                      </pre>
                    </ScrollArea>
                  </div>

                  {trace.error && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-destructive">Erreur</h4>
                      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                        {trace.error}
                      </div>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        </CardContent>
      </TooltipProvider>
    </Card>
  );
};
